import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { CartProvider } from "@/context/CartContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { COLLECTION_ROUTES } from "@/config/brand";
import Home from "@/pages/Home";
import CollectionPage from "@/pages/CollectionPage";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import EditorialPage from "@/pages/EditorialPage";
import AboutPage from "@/pages/AboutPage";
import StaticPage from "@/pages/StaticPage";

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/* The fixed header floats over the hero on Home; every other page clears it. */
const Main = ({ children }) => {
  const { pathname } = useLocation();
  return <main className={pathname === "/" ? "" : "pt-12 md:pt-16"}>{children}</main>;
};

const NotFound = () => (
  <div className="px-4 py-32 text-center" data-testid="not-found-page">
    <p className="font-anton-sc text-4xl tracking-[0.08em]">404</p>
    <p className="text-[11px] tracking-[0.2em] text-black/50 mt-4">PAGE NOT FOUND</p>
    <Link
      to="/"
      className="inline-block mt-8 text-[11px] font-medium tracking-[0.2em] border border-black px-10 py-4 hover:bg-black hover:text-white transition-colors"
    >
      BACK TO HOME
    </Link>
  </div>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <CartProvider>
          <ScrollManager />
          <Header />
          <Main>
            <Routes>
              <Route path="/" element={<Home />} />
              {COLLECTION_ROUTES.map((r) => (
                <Route
                  key={r.path}
                  path={r.path}
                  element={
                    <CollectionPage
                      key={r.path}
                      category={r.category}
                      title={r.title}
                    />
                  }
                />
              ))}
              <Route path="/products/:slug" element={<ProductPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/editorial" element={<EditorialPage />} />
              <Route
                path="/collections/fall-winter-26"
                element={<EditorialPage collection />}
              />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<StaticPage page="contact" />} />
              <Route path="/shipping" element={<StaticPage page="shipping" />} />
              <Route path="/returns" element={<StaticPage page="returns" />} />
              <Route path="/privacy" element={<StaticPage page="privacy" />} />
              <Route path="/terms" element={<StaticPage page="terms" />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Main>
          <Footer />
        </CartProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
